import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavbarProps {
  onEstimateClick: () => void;
}

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services/asphalt-paving" },
  { label: "About", href: "/about" },
  { label: "Locations", href: "/locations" },
];

export default function Navbar({ onEstimateClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a0a0a]/97 backdrop-blur-md border-b border-asphalt-700"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-14 flex items-center justify-between h-24">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/0119a1ee-e75d-405d-8b2a-d8ea08bbcc66/87302b8c-fb17-43ac-87c3-6be8795b2515_logo2.png?v=3c94924276f61998b62260eea2137fb0"
            alt="Premier Asphalt & Concrete"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <li key={link.href}>
              <Link
                to={link.href}
                className={`font-body text-xs font-700 uppercase tracking-widest2 transition-colors duration-200 cursor-pointer whitespace-nowrap relative group ${
                  location.pathname === link.href
                    ? "text-safety-500"
                    : "text-asphalt-200 hover:text-white"
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 w-0 h-px bg-safety-500 transition-all duration-300 group-hover:w-full ${location.pathname === link.href ? "w-full" : ""}`} />
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+13039971315"
            className="flex items-center gap-2 bg-safety-500 text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest px-6 py-3 hover:bg-safety-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill" />
            Call Now
          </a>
          <button
            onClick={onEstimateClick}
            className="flex items-center gap-2 border border-safety-500 text-safety-500 font-body font-700 text-xs uppercase tracking-widest px-6 py-3 hover:bg-safety-500 hover:text-asphalt-950 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line" />
            Get Estimate
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-safety-500 text-2xl cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={mobileOpen ? "ri-close-line" : "ri-menu-3-line"} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0a0a0a] border-t border-asphalt-700 px-6 py-8 flex flex-col gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="font-body text-xs font-700 uppercase tracking-widest2 text-asphalt-100 hover:text-safety-500 transition-colors cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-4 border-t border-asphalt-700">
            <a
              href="tel:+13039971315"
              className="flex items-center justify-center gap-2 bg-safety-500 text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest px-5 py-4 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              (303) 997-1315
            </a>
            <button
              onClick={onEstimateClick}
              className="flex items-center justify-center gap-2 border border-safety-500 text-safety-500 font-body font-700 text-xs uppercase tracking-widest px-5 py-4 cursor-pointer whitespace-nowrap"
            >
              Get Free Estimate
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
